import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usersControllerTriggerSweep } from '../../api/generated';
import { meQueryOptions } from '../../api/me';
import type { PendingQueueRow } from '../../api/pending';
import { approvePending, listPending, rejectPending } from '../../api/pending';
import { userPhotoQueryKey } from '../../api/users';
import { ErrorMessage } from '../common/ErrorMessage';
import { PhotoReviewDialog } from './PhotoReviewDialog';
import { LoadingSpinner } from './shared';

export function ModerationTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Four-eyes : une photo soumise par le lecteur lui-même ne peut pas être
  // approuvée par lui — le serveur refuse, le bouton le dit avant.
  const { data: me } = useQuery(meQueryOptions());
  const isOwnSubmission = useCallback(
    (p: PendingQueueRow) =>
      !!p.submittedByOid && !!me?.oid && p.submittedByOid === me.oid,
    [me?.oid],
  );
  const [items, setItems] = useState<PendingQueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sweeping, setSweeping] = useState(false);
  const [sweepMsg, setSweepMsg] = useState<string | null>(null);
  const [focusIdx, setFocusIdx] = useState(0);
  // Index into `items` of the photo under review, or null when the dialog is
  // closed. An index rather than the row itself: once a decision removes the
  // row, the same index is the next photo in the queue.
  const [reviewIdx, setReviewIdx] = useState<number | null>(null);
  const rowsRef = useRef<Map<string, HTMLLIElement | null>>(new Map());

  // `loading` covers the first fetch only, never a refresh: flipping it back on
  // after a decision would swap the open dialog for the spinner mid-review.
  const fetchItems = useCallback(async () => {
    setError(null);
    try {
      setItems(await listPending());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  // Keep both cursors in bounds whenever a new list arrives. Done during
  // render (not an effect) so derived state never lags a render behind.
  // Holding `reviewIdx` steady is what advances the dialog: the row that was
  // just decided is gone, so that index now holds the next one — and once it
  // runs past the end, the queue is done and the dialog closes.
  const [prevItems, setPrevItems] = useState(items);
  if (items !== prevItems) {
    setPrevItems(items);
    setFocusIdx((c) => Math.min(c, Math.max(0, items.length - 1)));
    setReviewIdx((c) => (c === null || c >= items.length ? null : c));
  }

  const handleSweep = async () => {
    setSweeping(true);
    setError(null);
    setSweepMsg(null);
    try {
      await usersControllerTriggerSweep({ throwOnError: true });
      setSweepMsg(t('admin.moderation.sweepScheduled'));
      void queryClient.invalidateQueries({
        queryKey: ['photos', 'pending', 'count'],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSweeping(false);
    }
  };

  // Keyboard nav : j/↓ next, k/↑ prev, Enter opens the review dialog. There is
  // deliberately no shortcut that approves or rejects — every decision goes
  // through the dialog, where the photo is actually visible.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (reviewIdx !== null) {
        return;
      }
      const target = e.target instanceof HTMLElement ? e.target : null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return;
      }
      if (target?.isContentEditable) {
        return;
      }
      if (items.length === 0) {
        return;
      }

      const goNext = e.key === 'ArrowDown' || e.key === 'j';
      const goPrev = e.key === 'ArrowUp' || e.key === 'k';
      const openReview = e.key === 'Enter';

      if (!goNext && !goPrev && !openReview) {
        return;
      }
      e.preventDefault();

      if (goNext) {
        setFocusIdx((c) => (c + 1) % items.length);
      } else if (goPrev) {
        setFocusIdx((c) => (c - 1 + items.length) % items.length);
      } else {
        setReviewIdx(focusIdx);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [items, focusIdx, reviewIdx]);

  // Scroll focused row into view when cursor moves
  useEffect(() => {
    const id = items[focusIdx]?.id;
    if (!id) {
      return;
    }
    const el = rowsRef.current.get(id);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusIdx, items]);

  const act = async (
    target: PendingQueueRow,
    action: 'approve' | 'reject',
    reason?: string,
  ) => {
    setActing(true);
    setError(null);
    try {
      // Failures throw an ApiError whose message carries the server's prose.
      if (action === 'approve') {
        await approvePending(target.id);
      } else {
        await rejectPending(target.id, reason);
      }
      await fetchItems();
      void queryClient.invalidateQueries({
        queryKey: ['photos', 'pending', 'count'],
      });
      // Approving writes the photo to Entra — drop its cached avatar so it
      // refetches everywhere.
      if (action === 'approve') {
        void queryClient.invalidateQueries({
          queryKey: userPhotoQueryKey(target.userOid),
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActing(false);
    }
  };

  const reviewItem = reviewIdx === null ? null : (items[reviewIdx] ?? null);

  const stepReview = (delta: number) => {
    setReviewIdx((c) =>
      c === null || items.length === 0
        ? c
        : (c + delta + items.length) % items.length,
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="display text-xl text-ink">
          {t('admin.moderation.title')}
        </h3>
        <button
          type="button"
          onClick={() => {
            void handleSweep();
          }}
          disabled={sweeping}
          className="btn-link text-[11px] disabled:opacity-50"
        >
          {sweeping
            ? t('admin.moderation.sweeping')
            : t('admin.moderation.sweep')}
        </button>
      </div>

      {sweepMsg && (
        <div className="mb-4 border border-green bg-green/12 px-3 py-2 text-xs text-green-ink">
          {sweepMsg}
        </div>
      )}

      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} size="xs" />
        </div>
      )}

      {items.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted">
          {t('admin.moderation.empty')}
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((p, idx) => (
            <li
              key={p.id}
              ref={(el) => {
                rowsRef.current.set(p.id, el);
              }}
              className="flex items-center gap-4 border border-rule bg-card p-4 -outline-offset-1"
              style={{
                outline: idx === focusIdx ? '2px solid var(--green)' : 'none',
              }}
            >
              <img
                src={p.thumbnailUrl}
                alt={p.userName ?? p.userUpn ?? 'Photo'}
                className="size-16 shrink-0 border border-ink object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="display truncate text-base text-ink">
                  {p.userName ?? p.userUpn ?? p.userOid}
                </p>
                {p.userUpn && p.userName && (
                  <p className="mono truncate text-xs text-muted">
                    {p.userUpn}
                  </p>
                )}
                <p className="label mt-[4px]">
                  {t('admin.moderation.submittedAt')}{' '}
                  {new Date(p.submittedAt).toLocaleString()}
                </p>
                {/* Qui a déposé la photo, quand ce n'est pas le sujet
                    lui-même (auto-soumission = redondant). */}
                {p.submittedByOid && p.submittedByOid !== p.userOid && (
                  <p className="label mt-[2px]">
                    {t('admin.moderation.submittedBy')}{' '}
                    {p.submittedByName ?? p.submittedByUpn ?? p.submittedByOid}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setFocusIdx(idx);
                  setReviewIdx(idx);
                }}
                disabled={acting}
                className="btn-primary disabled:opacity-50"
              >
                {t('admin.moderation.review.open')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* The only place a decision is taken */}
      <PhotoReviewDialog
        item={reviewItem}
        index={reviewIdx ?? 0}
        total={items.length}
        canApprove={reviewItem !== null && !isOwnSubmission(reviewItem)}
        busy={acting}
        error={error}
        onApprove={() => {
          if (reviewItem) {
            void act(reviewItem, 'approve');
          }
        }}
        onReject={(reason) => {
          if (reviewItem) {
            void act(reviewItem, 'reject', reason);
          }
        }}
        onPrev={() => {
          stepReview(-1);
        }}
        onNext={() => {
          stepReview(1);
        }}
        onClose={() => {
          setReviewIdx(null);
        }}
      />
    </div>
  );
}
